import { useModal } from '@ebay/nice-modal-react'
import { Link, useNavigate } from '@tanstack/router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { styled } from '@linaria/react'

import avatar_3 from '../../assets/common/avatar_3.webp'
import coin_anim from '../../assets/common/coin_anim.gif'
import useGeoCheck from '../../hooks/useGeoCheck'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import { api } from '../../utils/api-client'
import { numberWithCommas } from '../../utils/number'
import Icon from '../icon'
import WalletManagementModal from '../wallet-management/wallet-management-modal'
import LoginModal from './log-in-modal'
import LogoutModal from './log-out-modal'
import useRefreshBalance from '../../hooks/useRefreshBalance'
import { CASINO_CATEGORIES } from '../../constants/casino'
import LanguageSelector from './language-selector'

const Nav = styled.div`
  width: 100%;
  background-color: var(--primary);
  box-shadow: 0px 1px 5px rgba(0, 0, 0, 0.1);
  padding: 0 40px;
  position: sticky;
  top: 0;
  z-index: 100;
  height: 60px;
  @media (min-width: 1400px) {
    padding: 0 90px;
  }
  @media (min-width: 1600px) {
    padding: 10px 150px;
  }
  @media (min-width: 1800px) {
    padding: 10px 250px;
  }
`

const Logo = styled.img`
  width: 100px;
  margin-right: 30px;
  aspect-ratio: 471 / 104;
`

const StyledLink = styled(Link)`
  font-weight: 600;
  font-size: 16;
  color: var(--highlight);
  position: relative;
  text-transform: uppercase;
  &:before {
    content: '';
    width: 0%;
    height: 1px;
    opacity: 1;
    background-color: var(--highlight);
    position: absolute;
    bottom: -1px;
    left: -5%;
    transition: 0.2s ease-in-out;
  }
  &:hover {
    &:before {
      width: 110%;
    }
  }
`

const Btn = styled.span`
  height: 35px;
  background-color: var(--primary-btn-bg);
  border-radius: 6px;
  padding: 0 12px;
  p {
    font-size: 12px;
    font-family: 'Poppins';
    font-weight: 300;
    color: var(--primary-btn-txt);
    text-transform: uppercase;
  }
  &.login {
    background-color: var(--highlight);
    margin-right: 8px;
  }
`

const PrimaryBtn = styled.button`
  text-align: center;
  font-size: 12px;
  padding: 10px 17px;
  color: #fff;
  border-radius: 2px;
  transition: all 0.2s ease-in-out;
  display: block;
  font-family: 'Syncopate-Bold';
  text-transform: uppercase;
  &.deposit {
    background-color: var(--highlight);
    color: #fff;
  }
`

const WalletIconContainer = styled.div`
  margin-right: 6px;
  position: relative;
  svg {
    fill: var(--txt);
  }
`

const Coins = styled.img`
  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
  position: absolute;
  top: -32px;
  left: -165px;
  width: 150px;
  animation: fadeIn 0.2s ease-in-out;
  pointer-events: none;
  transform: rotate(30deg);
`

const Balance = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: var(--txt);
`

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  background-color: transparent;
  overflow: hidden;
  border-radius: 50%;
  margin-right: 15px;
  background-color: #fff;
  .theme-dark & {
    border: 1px solid #fff;
  }
  .theme-light & {
    border: 1px solid #ccc;
  }
  img {
    width: 30px;
    height: 30px;
    object-fit: cover;
  }
`

const Refresh = styled.button`
  display: flex;
  padding: 6px;
  margin-right: 9px;
  @keyframes rotate {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
  &.rotate {
    animation: rotate 1.5s linear infinite;
  }
`

const Logout = styled.button`
  display: flex;
  fill: var(--txt);
`

interface NavbarProps {
  hideBalance?: boolean
}

const Navbar = ({ hideBalance }: NavbarProps) => {
  const isCoinAnimVisible = useBoundStore(
    state => state.common.isCoinAnimVisible,
  )
  const profile_picture = useBoundStore(state => state.user.profile_picture)
  const setProfilePicture = useBoundStore(state => state.user.setProfilePicture)
  const unified_balance = useBoundStore(state => state.user.unified_balance)
  const username = useBoundStore(state => state.user.username)
  const navigate = useNavigate()
  const { geoCheck } = useGeoCheck()
  const logInModal = useModal(LoginModal)
  const logOutModal = useModal(LogoutModal)
  const walletManagementModal = useModal(WalletManagementModal)
  const { t } = useTranslation(['common', 'drawer', 'walletManagement'])
  const { refreshBalance, isLoading: balanceLoading } = useRefreshBalance()
  const [profileError, setProfileError] = useState(false)

  useEffect(() => {
    if (!username) return
    refreshBalance()

    const fetchProfilePicture = async () => {
      try {
        const response = await api.post<ApiResponse<string>>('/ajax_data.php', {
          type: 'get_imgurl',
        })
        const { status, info } = response.data
        if (status === 1) {
          setProfilePicture(info)
        }
      } catch (error) {
        console.error(error)
      }
    }
    fetchProfilePicture()
  }, [username])

  return (
    <Nav className="flx-btw-ctr">
      <div
        className="flx-ctr"
        style={{ flex: 1, justifyContent: 'flex-start', marginRight: 80 }}>
        <Link to="/" className="flx-ctr">
          <Logo
            src={
              'https://sfgdefe0923.fun88adrods.online/mexicoimages/logo/dark.webp'
            }
            alt="Company Logo"
          />
        </Link>
      </div>
      <div className="flx-btw-ctr" style={{ marginRight: 100, width: '700px' }}>
        <StyledLink to="/sports/bti">{t('drawer:sports')} 1</StyledLink>
        <StyledLink to="/sports">{t('drawer:sports')} 2</StyledLink>
        <StyledLink
          to="/casino/$category"
          params={{ category: CASINO_CATEGORIES[0].label }}>
          CASINO
        </StyledLink>
        <StyledLink to="/promotions">{t('drawer:promotions')}</StyledLink>
      </div>
      <div className="flx-ctr" style={{ flex: 1, justifyContent: 'flex-end' }}>
        {username ? (
          <>
            {!hideBalance && (
              <>
                <div
                  style={{ cursor: 'pointer' }}
                  className="flx-ctr"
                  onClick={() =>
                    walletManagementModal.show({
                      walletManagementDefaultTab: { tab: 0 },
                    })
                  }>
                  <WalletIconContainer>
                    <Icon id="wallet" width="22" height="22" />
                    {isCoinAnimVisible && <Coins src={coin_anim} />}
                  </WalletIconContainer>
                  <Balance>
                    ${numberWithCommas(parseFloat(unified_balance))}
                  </Balance>
                </div>
                <Refresh
                  onClick={refreshBalance}
                  className={balanceLoading ? 'rotate' : ''}>
                  <Icon
                    id="refresh-balance"
                    width="17"
                    height="17"
                    fill="#D2D4D6"
                  />
                </Refresh>
              </>
            )}
            <PrimaryBtn
              className="deposit"
              style={{ marginRight: '15px' }}
              onClick={() => {
                walletManagementModal.show({
                  walletManagementDefaultTab: { page: 0, tab: 0 },
                })
              }}>
              {t('deposit', { ns: 'drawer' })}
            </PrimaryBtn>
            <Link to="/profile">
              <Avatar>
                <img
                  src={
                    profile_picture && !profileError
                      ? profile_picture
                      : avatar_3
                  }
                  alt="Avatar"
                  onError={() => setProfileError(true)}
                />
              </Avatar>
            </Link>
            <Logout
              className="flx-ctr"
              style={{ cursor: 'pointer' }}
              onClick={() => logOutModal.show()}>
              <Icon id="logout" width="22" height="20" />
            </Logout>
          </>
        ) : (
          <>
            <Btn
              className="flx-ctr login"
              style={{ marginRight: '10px' }}
              onClick={() => geoCheck(logInModal.show)}>
              <p>{t('login')}</p>
            </Btn>
            <Btn
              className="flx-ctr"
              onClick={() =>
                geoCheck(() =>
                  navigate({
                    to: '/register',
                  }),
                )
              }>
              <p>{t('register')}</p>
            </Btn>
            <LanguageSelector />
          </>
        )}
      </div>
    </Nav>
  )
}

export default Navbar
